import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { format, formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';
import { ArrowLeft, CheckCircle, Clock, SlidersHorizontal, Trash2 } from 'lucide-react';
import { Button } from '../ui/Button';
import { ToolbarSearch } from '../ui/ListToolbar';
import { Spinner } from '../ui/Spinner';
import { TicketStatusBadge } from './TicketStatusBadge';
import { TicketCategoryBadge } from './TicketCategoryBadge';
import { TicketPoleBadge } from './TicketPoleBadge';
import { getDisplayStatus } from './ticketStatusDisplay';
import { parseTicketMessage } from './TicketsListPage';
import {
  clientFollowUpDisplay,
  isTicketValidated,
} from '../../services/tickets/clientFollowUp';
import { useTickets, useDeleteTicket } from '../../hooks/queries/useTickets';
import { useCanAccessFeature } from '../../hooks/usePermissions';
import { cn } from '../../lib/cn';

import { ResizableColumns } from '../ui/ResizableColumns';
export function TicketsArchivePage() {
  const navigate = useNavigate();
  const deleteTicket = useDeleteTicket();
  const canDelete = useCanAccessFeature('tickets_delete');

  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchInput || ''), 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const { data: tickets, isLoading } = useTickets({
    archived_only: true,
    search: debouncedSearch || undefined,
  });

  const handleRowClick = useCallback(
    (ticketId: string) => navigate(`/tickets/${ticketId}`),
    [navigate],
  );

  return (
    <div className="main-container-gdm space-y-3">
      {/* Header */}
      <div className="flex items-start gap-3 flex-wrap">
        <button
          onClick={() => navigate('/tickets')}
          className="flex items-center gap-1.5 text-sm text-neutral-500 hover:text-neutral-700 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Retour aux demandes
        </button>
      </div>

      <ToolbarSearch value={searchInput} onChange={setSearchInput} placeholder="Rechercher..." />

      {/* Table */}
      <div className="bg-cime-surface rounded-cime border border-neutral-200 shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="flex justify-center items-center py-16">
            <Spinner />
          </div>
        ) : !tickets || tickets.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
            <SlidersHorizontal className="w-10 h-10 text-neutral-300 mb-3" />
            <p className="text-neutral-400 max-w-sm">
              {debouncedSearch
                ? 'Aucun ticket archivé ne correspond à la recherche.'
                : 'Aucun ticket archivé pour le moment.'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="cime-table text-[13px]" role="table">
              <ResizableColumns id="tickets-archive" />
              <thead>
                <tr>
                  <th className="text-left whitespace-nowrap px-2 py-[9px] first:pl-[18px] last:pr-[18px]">
                    Demande
                  </th>
                  <th className="text-left whitespace-nowrap px-2 py-[9px] first:pl-[18px] last:pr-[18px]">
                    Expéditeur
                  </th>
                  <th className="text-left whitespace-nowrap px-2 py-[9px] first:pl-[18px] last:pr-[18px] hidden lg:table-cell">
                    Pôle
                  </th>
                  <th className="text-left whitespace-nowrap px-2 py-[9px] first:pl-[18px] last:pr-[18px] hidden lg:table-cell">
                    Catégorie
                  </th>
                  <th className="text-left whitespace-nowrap px-2 py-[9px] first:pl-[18px] last:pr-[18px] hidden lg:table-cell">
                    État client
                  </th>
                  <th className="text-left whitespace-nowrap px-2 py-[9px] first:pl-[18px] last:pr-[18px] hidden lg:table-cell">
                    Validé
                  </th>
                  <th className="text-left whitespace-nowrap px-2 py-[9px] first:pl-[18px] last:pr-[18px]">
                    Statut
                  </th>
                  <th className="text-left whitespace-nowrap px-2 py-[9px] first:pl-[18px] last:pr-[18px]">
                    Reçue le
                  </th>
                  <th className="w-10 px-2 py-[9px]" aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {tickets.map((ticket) => {
                  const timeAgo = formatDistanceToNow(new Date(ticket.created_at), {
                    addSuffix: true,
                    locale: fr,
                  });
                  const fullDate = format(
                    new Date(ticket.created_at),
                    "d MMM yyyy · HH:mm",
                    { locale: fr },
                  );

                  return (
                    <tr
                      key={ticket.id}
                      onClick={() => handleRowClick(ticket.id)}
                      className="cursor-pointer transition-colors opacity-75 hover:opacity-100"
                    >
                      {/* Excerpt */}
                      <td className="px-2 py-[11px] first:pl-[18px] last:pr-[18px] max-w-xs">
                        {(() => {
                          const { subject, body } = parseTicketMessage(ticket.message);
                          return (
                            <>
                              <p className="font-semibold text-neutral-800 truncate text-[13px]">
                                {subject ?? ticket.ai_summary ?? body.slice(0, 60)}
                              </p>
                              <p className="text-cime-text-muted truncate text-xs mt-0.5">
                                {body.slice(0, 80)}{body.length > 80 ? '…' : ''}
                              </p>
                            </>
                          );
                        })()}
                      </td>

                      {/* Sender */}
                      <td className="px-2 py-[11px] first:pl-[18px] last:pr-[18px]">
                        <p className="font-semibold text-[13px] text-neutral-900 truncate max-w-36">
                          {ticket.sender_name ?? 'Inconnu'}
                        </p>
                        <p className="text-xs text-neutral-400 truncate max-w-36">
                          {ticket.sender_email ?? ''}
                        </p>
                      </td>

                      {/* Pole */}
                      <td className="px-2 py-[11px] first:pl-[18px] last:pr-[18px] hidden lg:table-cell whitespace-nowrap">
                        <TicketPoleBadge pole={ticket.category?.pole ?? null} />
                      </td>

                      {/* Category */}
                      <td className="px-2 py-[11px] first:pl-[18px] last:pr-[18px] hidden lg:table-cell whitespace-nowrap">
                        <TicketCategoryBadge category={ticket.category} />
                      </td>

                      {/* Client status */}
                      <td className="px-2 py-[11px] first:pl-[18px] last:pr-[18px] hidden lg:table-cell whitespace-nowrap">
                        {(() => {
                          const followUp = clientFollowUpDisplay(ticket.client_status);
                          return followUp ? (
                            <span
                              className={cn(
                                'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium',
                                followUp.className,
                              )}
                            >
                              {followUp.label}
                            </span>
                          ) : (
                            <span className="text-neutral-300">—</span>
                          );
                        })()}
                      </td>

                      {/* Validé = transformé en affaire */}
                      <td className="px-2 py-[11px] first:pl-[18px] last:pr-[18px] hidden lg:table-cell whitespace-nowrap">
                        {isTicketValidated(ticket) ? (
                          <CheckCircle className="w-4 h-4 text-[#2f7a55]" />
                        ) : (
                          <Clock className="w-4 h-4 text-neutral-300" />
                        )}
                      </td>

                      {/* Status */}
                      <td className="px-2 py-[11px] first:pl-[18px] last:pr-[18px] whitespace-nowrap">
                        <TicketStatusBadge status={getDisplayStatus(ticket)} />
                      </td>

                      {/* Reçue le : toujours la dernière colonne */}
                      <td className="px-2 py-[11px] first:pl-[18px] last:pr-[18px] whitespace-nowrap leading-tight">
                        <span className="block text-[13px] text-cime-text-primary">{fullDate}</span>
                        <span className="block text-xs text-neutral-400">{timeAgo}</span>
                      </td>

                      {/* Actions */}
                      <td className="px-2 py-[11px] first:pl-[18px] last:pr-[18px]" onClick={(e) => e.stopPropagation()}>
                        {canDelete && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="opacity-0 group-hover:opacity-100 transition-opacity text-red-500 hover:text-red-700 hover:bg-red-50"
                            disabled={deleteTicket.isPending}
                            onClick={() => {
                              if (
                                window.confirm(
                                  'Supprimer définitivement ce ticket ? Cette action est irréversible.',
                                )
                              ) {
                                deleteTicket.mutate(ticket.id);
                              }
                            }}
                            aria-label={`Supprimer le ticket de ${ticket.sender_name ?? 'Inconnu'}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
